import { client } from "helper/apolloConfig";
import { globalVariables } from "helper/globalVariables";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { updateMemberProfileMutation } from "redux/features/memberSlice/memberGql";
import { setMemberDetails } from "redux/features/memberSlice/memberSlice";
import { useUploadImage } from "redux/hooks";
import { FileUpload } from "shared/Components/fileUpload";
import { PhoneInput } from "shared/Components/phoneInput";
import { SearchLocation } from "shared/Components/searchLocation";
import { TextInput } from "shared/Components/textInput";
import { TextArea } from "shared/Components/textarea";
import { registerEditMemberResolver } from "validators/UserValidation";
import { EmailChangeConfirmationModal } from "./EmailChangeConfirmationModal";

export const MemberProfile = forwardRef((props, ref) => {
  const { onSetLoading, isEditable, onProfileSuccess, onProfileError } =
    props || {};
  const { onUploadImage, imageUrl } = useUploadImage();
  const [memberImage, setMemberImage] = useState("");
  const formRef = useRef(null);
  const emailConfirmModalRef = useRef(null);
  const dispatch = useDispatch();
  const { auth } = useSelector((state) => state);
  const [cityFilter, setCityFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [checkValidation, setCheckValidation] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);
  const [originalEmail, setOriginalEmail] = useState("");

  useImperativeHandle(ref, () => ({
    saveChanges() {
      onCheckValidation();
    },
  }));

  useEffect(() => {
    getMemberProfile();
  }, []);

  useEffect(() => {
    if (imageUrl) {
      setMemberImage(imageUrl);
    }
  }, [imageUrl]);

  const getMemberProfile = async () => {
    if (auth?.memberReducer?.memberDetails) {
      const memberProfile = auth?.memberReducer?.memberDetails;
      setValue("firstName", memberProfile?.first_name);
      setValue("lastName", memberProfile?.last_name);
      setValue("email", memberProfile?.email);
      setOriginalEmail(memberProfile?.email); // Store original email
      setValue("location", memberProfile?.location_address);
      setValue("phoneNumber", memberProfile?.phone_number);
      setValue("emergencyContactName", memberProfile?.emergency_contact_name);
      setValue("zip", memberProfile?.zipCode);
      setValue(
        "emergencyContactNumber",
        memberProfile?.emergency_contact_number
      );
      setMemberImage(memberProfile?.profile);
      setCityFilter(memberProfile?.city);
      setStateFilter(memberProfile?.state);
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    resolver: registerEditMemberResolver,
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      location: "",
      phoneNumber: "",
      emergencyContactName: "",
      emergencyContactNumber: "",
      zip: "",
    },
  });

  const onHandleSubmit = async (credentials) => {
    // Check if email has changed
    const emailChanged = credentials?.email !== originalEmail;

    if (emailChanged) {
      // Store the form data and show confirmation modal
      setPendingFormData(credentials);
      emailConfirmModalRef.current?.openModal();
      return; // Don't proceed with save yet
    }

    // If email hasn't changed, proceed with normal save
    await performSave(credentials);
  };

  const performSave = async (credentials) => {
    onSetLoading(true);

    const payload = {
      first_name: credentials?.firstName,
      last_name: credentials?.lastName,
      email: credentials?.email,
      location_address: credentials?.location,
      phone_number: credentials?.phoneNumber,
      emergency_contact_name: credentials?.emergencyContactName,
      emergency_contact_number: credentials?.emergencyContactNumber,
      profile: memberImage,
      zipCode: credentials?.zip,
      city: cityFilter,
      state: stateFilter,
    };

    try {
      const result = await client.mutate({
        mutation: updateMemberProfileMutation,
        variables: { data: payload },
      });

      if (result?.data?.updateMyMemberProfile) {
        onProfileSuccess?.();
        dispatch(setMemberDetails(result?.data?.updateMyMemberProfile));
        setOriginalEmail(credentials?.email); // Update original email after successful save
        getMemberProfile();
      }
      onSetLoading(false);
    } catch (error) {
      onProfileError?.(error);
      onSetLoading(false);
    }
  };

  const handleEmailChangeConfirm = async () => {
    // Close the modal
    emailConfirmModalRef.current?.closeModal();

    // Proceed with the save using the pending form data
    if (pendingFormData) {
      await performSave(pendingFormData);
      setPendingFormData(null);
    }
  };

  const handleEmailChangeCancel = () => {
    // Close the modal and discard pending changes
    emailConfirmModalRef.current?.closeModal();
    setPendingFormData(null);
  };

  const onCheckValidation = () => {
    if (!cityFilter || !stateFilter) {
      setCheckValidation(true);
      const missing = [];
      if (!stateFilter) missing.push("State");
      if (!cityFilter) missing.push("City");
      onProfileError?.({ message: `Please fill in the required field(s): ${missing.join(", ")}.` });
      return;
    }

    handleSubmit(onHandleSubmit)();
  };

  const onValidate = () => {
    if (cityFilter && stateFilter) {
      setCheckValidation(false);
    } else {
      setCheckValidation(true);
    }
  };
  console.log(memberImage);
  return (
    <>
      <div className="w-full flex justify-center align-center">
        <span className="text-2xl md:text-3xl font-semibold text-gray-900 text-center mb-6">
          {globalVariables.memberDetails}
        </span>
      </div>
      <FileUpload
        onSetImage={(img) => onUploadImage(img)}
        defaultImage={memberImage}
        disabled={!isEditable}
      />
      <div className="mt-6" />
      <form
        className="flex justify-center"
        onSubmit={handleSubmit(onHandleSubmit)}
        ref={formRef}
      >
        <div className="max-[400px]:w-[90%]">
          <TextInput
            register={register}
            name="firstName"
            placeholder={globalVariables.enterFirstName}
            errorMsg={errors.firstName?.message}
            disabled={!isEditable}
            label={globalVariables.firstName}
          />
          <div className="mt-5" />
          <TextInput
            register={register}
            name="lastName"
            placeholder={globalVariables.enterLastName}
            errorMsg={errors.lastName?.message}
            disabled={!isEditable}
            label={globalVariables.lastName}
          />
          <div className="mt-5" />
          <TextInput
            register={register}
            name="email"
            placeholder={globalVariables.enterEmailAddress}
            errorMsg={errors.email?.message}
            disabled={!isEditable}
            label={globalVariables.emailAddress}
            textTransform="normal-case"
          />
          <div className="mt-5" />
          <TextArea
            register={register}
            name="location"
            placeholder={globalVariables.enterLocationAddress}
            errorMsg={errors.location?.message}
            disabled={!isEditable}
            label={globalVariables.address}
          />
          <div className="mt-5" />
          <SearchLocation
            label={globalVariables.state}
            width={"w-[358px] lg:w-[384px] max-[400px]:w-[100%]"}
            name="State"
            types={["(regions)"]}
            placeholder={globalVariables.state}
            onSelectLocation={(location) => {
              setStateFilter(location);
              onValidate();
            }}
            disabled={!isEditable}
            errorMsg={
              checkValidation &&
              !stateFilter &&
              globalVariables.pleaseSelectState
            }
            defaultValue={stateFilter}
          />
          <div className="mt-5" />
          <SearchLocation
            label={globalVariables.city}
            width={"w-[358px] lg:w-[384px] max-[400px]:w-[100%]"}
            name="city"
            placeholder={globalVariables.city}
            types={["(cities)"]}
            onSelectLocation={(location) => {
              setCityFilter(location);
              onValidate();
            }}
            disabled={!isEditable}
            errorMsg={
              checkValidation && !cityFilter && globalVariables.pleaseSelectCity
            }
            helperText={globalVariables.cityHelperText}
            defaultValue={cityFilter}
            searchBy={stateFilter}
          />

          <div className="mt-5" />
          <TextInput
            register={register}
            name="zip"
            placeholder={globalVariables.zip}
            label={globalVariables.zip}
            errorMsg={errors.zip?.message}
            type="number"
            disabled={!isEditable}
          />
          <div className="mt-5" />
          <PhoneInput
            name="phoneNumber"
            placeholder={globalVariables.enterPhoneNumber}
            errorMsg={errors.phoneNumber?.message}
            disabled={!isEditable}
            label={globalVariables.phoneNumber}
            value={watch("phoneNumber")}
            onChange={(e) => setValue("phoneNumber", e.target.value)}
          />

          <div className="mt-5" />
          <TextInput
            register={register}
            name="emergencyContactName"
            placeholder={globalVariables.enterEmergencyContactName}
            errorMsg={errors.emergencyContactName?.message}
            disabled={!isEditable}
            label={globalVariables.emergencyContactName}
          />

          <div className="mt-5" />
          <PhoneInput
            name="emergencyContactNumber"
            placeholder={globalVariables.enterEmergencyPhoneNumber}
            errorMsg={errors.emergencyContactNumber?.message}
            disabled={!isEditable}
            label={globalVariables.emergencyPhoneNumber}
            value={watch("emergencyContactNumber")}
            onChange={(e) => setValue("emergencyContactNumber", e.target.value)}
          />
        </div>
      </form>

      <EmailChangeConfirmationModal
        modalRef={emailConfirmModalRef}
        newEmail={pendingFormData?.email}
        onConfirm={handleEmailChangeConfirm}
        onCancel={handleEmailChangeCancel}
      />
    </>
  );
});
